/**
 * SurveyResultsCallout Component
 * Purple callout banner showing survey result counts with "Review Feedback" button.
 * Renders below the finalization banner in the Sessions tab.
 */

import { useState, useEffect } from 'react';
import { Alert, Group, Button, Text } from '@mantine/core';
import { FileText, BarChart2 } from 'react-feather';
import { feedbackSurveysApi, type SurveyResultsSummary } from '../../../../services/api/feedbackSurveys';

interface SurveyResultsCalloutProps {
  cohortId: string;
  onReviewClick: () => void;
}

export function SurveyResultsCallout({ cohortId, onReviewClick }: SurveyResultsCalloutProps) {
  const [summary, setSummary] = useState<SurveyResultsSummary | null>(null);

  useEffect(() => {
    let cancelled = false;
    feedbackSurveysApi.getResultsSummary(cohortId).then((data) => {
      if (!cancelled) setSummary(data);
    }).catch(() => {
      // Silently fail - banner just won't show
    });
    return () => { cancelled = true; };
  }, [cohortId]);

  if (!summary || !summary.has_results) return null;

  const typeBreakdown: string[] = [];
  if (summary.by_type.post_session > 0) {
    typeBreakdown.push(`${summary.by_type.post_session} Post-Session`);
  }
  if (summary.by_type.midpoint > 0) {
    typeBreakdown.push(`${summary.by_type.midpoint} Midpoint`);
  }
  if (summary.by_type.endpoint > 0) {
    typeBreakdown.push(`${summary.by_type.endpoint} Endpoint`);
  }

  return (
    <Alert
      icon={
        <div style={{
          backgroundColor: 'rgba(139, 92, 246, 0.15)',
          borderRadius: '50%',
          width: 32,
          height: 32,
          minWidth: 32,
          minHeight: 32,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <FileText size={18} color="#8B5CF6" />
        </div>
      }
      mb="md"
      styles={{
        root: { backgroundColor: '#F4EEFF', border: 'none' },
        icon: { backgroundColor: 'transparent' },
      }}
    >
      <Group justify="space-between" align="center" wrap="nowrap">
        <Group gap={4} align="center">
          <Text fw={600} c="#8B5CF6" size="sm">
            {summary.total} Survey Results Submitted
          </Text>
          {typeBreakdown.length > 0 && (
            <>
              <Text c="#8B5CF6" size="sm">&mdash;</Text>
              <Text c="#000000" size="sm">{typeBreakdown.join(', ')}</Text>
            </>
          )}
        </Group>
        <Button
          leftSection={<BarChart2 size={14} />}
          onClick={onReviewClick}
          size="sm"
          styles={{
            root: { backgroundColor: '#8B5CF6', color: '#FFFFFF' },
          }}
        >
          Review Feedback
        </Button>
      </Group>
    </Alert>
  );
}

export default SurveyResultsCallout;
